// Конструктор КП — раздел портала 55, перенесённый в CRM. ТОЛЬКО ЧТЕНИЕ.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ
// Сохранения в историю и отправки в сделку нет. Это не забытая кнопка: новые разделы CRM
// выходят сначала ТОЛЬКО НА ЧТЕНИЕ, а сохранённое КП — это запись, и она цепляется к
// бизнес-процессам (номер КП, привязка к сделке, кто согласовал). История КП — на портале 55.
//
// ЧТО ЗДЕСЬ ЕСТЬ. Печать бланка и выгрузка в Word. Печать записью не является: лист
// собирается в браузере из того, что человек уже набрал на экране, и никуда не уходит —
// ни в 1С, ни в Bitrix, ни в ядро. Коммерческое предложение — единственная из трёх наших
// печатных форм, которую можно напечатать ПОЛНОСТЬЮ: данные 1С ей не нужны вовсе, позиции
// и условия вводит человек. (Официальный УПД, для сравнения, из нашего зеркала не выходит:
// зеркало отражает УТ, а бланк печатается из БП, и моста между базами нет.)
//
// Расчёт живёт в lib/prices.ts, а не здесь: это деньги, которые уедут клиенту, и они
// обязаны проверяться тестами без отрисовки. Порядок действий (наценка → сумма → скидка →
// НДС) и арифметика НДС взяты у портала 55 буквально, чтобы суммы совпадали.
// Сборка бланка — в lib/print1c.ts (`кпИзКонструктора` и `листКП`), по той же причине.
import { useMemo, useState } from 'react'

import { useПозицииПрайса, useСправочникиПрайсов } from '../api/prices'
import type { ПозицияПрайса } from '../api/prices'
import { MIN_QUERY } from '../api/stock'
import { QueryState } from '../components/QueryState'
import { fmtDate, plural, todayIso } from '../lib/format'
import { кпИзКонструктора, листКП, открытьЛист, скачатьДок } from '../lib/print1c'
import {
  СТАВКИ_НДС, УСЛОВИЯ_ПО_УМОЛЧАНИЮ, УСЛОВИЯ_ТЕКСТОМ, ШАПКА_ПУСТАЯ,
  вСтрокуКП, годнаДляКП, итогиКП, копейки, подписьНДС, почемуНеГодна, текстКП, цена,
  ценаСтроки,
} from '../lib/prices'
import type { СтрокаКП, УсловияКП, ШапкаКП } from '../lib/prices'

/** Сколько позиций прайса показывать в поиске. Больше в один экран всё равно не читают. */
const НАЙТИ = 20

export default function QuoteBuilder() {
  const [строки, setСтроки] = useState<СтрокаКП[]>([])
  const [у, setУ] = useState<УсловияКП>(УСЛОВИЯ_ПО_УМОЛЧАНИЮ)
  const [ш, setШ] = useState<ШапкаКП>({
    ...ШАПКА_ПУСТАЯ,
    условияОплаты: УСЛОВИЯ_ТЕКСТОМ.оплата,
    условияПоставки: УСЛОВИЯ_ТЕКСТОМ.поставка,
  })
  const [ввод, setВвод] = useState('')
  const [ручноеИмя, setРучноеИмя] = useState('')
  const [ручнаяЦена, setРучнаяЦена] = useState('')
  const [ручноеКол, setРучноеКол] = useState('1')
  const [скопировано, setСкопировано] = useState('')
  // Дата и вид документа живут здесь, а не в ШапкаКП: ШапкаКП — это текст письма
  // (lib/prices.ts), а дата и «оферта / не оферта» нужны только бланку.
  // Сегодняшняя дата подставлена В ПОЛЕ, а не в лист: человек её видит и может поменять.
  // Это не то же самое, что «лист подставил сегодня, если даты нет», — так делать нельзя.
  const [дата, setДата] = useState(todayIso())
  const [оферта, setОферта] = useState(true)
  const [бедаПечати, setБедаПечати] = useState('')

  const refs = useСправочникиПрайсов()
  const q = ввод.trim()
  const короткий = q.length < MIN_QUERY
  const поиск = useПозицииПрайса({ q: q || undefined, limit: НАЙТИ }, !короткий)

  const итоги = useMemo(() => итогиКП(строки, у), [строки, у])
  const текст = useMemo(() => текстКП(строки, у, ш), [строки, у, ш])

  const добавить = (п: ПозицияПрайса) => {
    setСкопировано('')
    // Один и тот же артикул в КП бывает нужен дважды (разная комплектация, разный срок),
    // поэтому дубли не запрещаем — но ключ строки делаем уникальным, иначе React
    // перепутает строки при удалении.
    setСтроки((прежние) => [...прежние, { ...вСтрокуКП(п), key: `${п.src_key}#${прежние.length}` }])
  }

  const добавитьРучную = () => {
    const имя = ручноеИмя.trim()
    if (!имя) return
    const ц = Number(ручнаяЦена.replace(',', '.'))
    setСтроки((прежние) => [...прежние, {
      key: `руч-${прежние.length}-${Date.now()}`,
      name: имя,
      pn: '',
      base: null,
      cur: 'RUB',
      manual: Number.isFinite(ц) && ц > 0 ? копейки(ц) : null,
      qty: Math.max(1, Number(ручноеКол) || 1),
      unit: 'шт',
      problems: [],
      сама: true,
    }])
    setРучноеИмя('')
    setРучнаяЦена('')
    setРучноеКол('1')
    setСкопировано('')
  }

  const править = (ключ: string, что: Partial<СтрокаКП>) =>
    setСтроки((прежние) => прежние.map((с) => (с.key === ключ ? { ...с, ...что } : с)))

  const убрать = (ключ: string) =>
    setСтроки((прежние) => прежние.filter((с) => с.key !== ключ))

  /**
   * Собрать бланк. Отдельно от вывода: печать и .doc делят одну сборку — иначе на бумаге
   * и в файле однажды окажутся разные листы.
   */
  const собратьЛист = (): { html: string; имя: string } => {
    const { кп, чегоНет } = кпИзКонструктора(строки, у, ш, { дата, оферта })
    const о = refs.data?.last_import
      ? `Цены взяты из переноса прайса №${refs.data.last_import.id} от `
        + `${fmtDate(refs.data.last_import.started_at, true)}. ${refs.data.data_notes.no_fx}`
      : 'Прайсы в ядро не залиты: все цены в этом предложении введены руками.'
    return {
      html: листКП(кп, { чегоНет: [...чегоНет, о] }),
      имя: `КП ${ш.номер || дата}`.trim(),
    }
  }

  const напечатать = () => {
    setБедаПечати('')
    if (открытьЛист(собратьЛист().html) === 'blocked') {
      setБедаПечати('Браузер не дал открыть окно печати. Разрешите всплывающие окна для'
        + ' этого адреса и нажмите ещё раз — лист открывается отдельной страницей.')
    }
  }

  const вWord = () => {
    setБедаПечати('')
    const л = собратьЛист()
    if (скачатьДок(л.html, л.имя) === 'нет-браузера') {
      setБедаПечати('Сохранить файл не удалось: браузер не дал создать ссылку на скачивание.')
    }
  }

  const скопировать = async () => {
    try {
      await navigator.clipboard.writeText(текст)
      setСкопировано('Текст КП скопирован в буфер обмена.')
    } catch {
      // Буфер обмена может быть недоступен (нет разрешения, старый браузер, http).
      // Текст всё равно виден ниже и выделяется руками — так что это не отказ.
      setСкопировано('Браузер не дал доступ к буферу — выделите текст ниже и скопируйте вручную.')
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Конструктор КП</h1>
        <div className="spacer" />
        <span className="muted">
          позиций в предложении: {строки.length}
          {итоги.безЦены > 0 ? ` · без цены: ${итоги.безЦены}` : ''}
        </span>
      </div>

      <div className="notice">
        <b>Сохранение появится отдельным шагом.</b> Раздел читает прайс, считает предложение
        и печатает бланк, но в CRM собранное КП не сохраняется и в сделку не уходит: это
        запись, а новые разделы выходят сначала только на чтение. История КП — на портале 55.
        Печать записью не является: лист собирается в браузере и никуда не отправляется.
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {refs.data?.last_import ? (
          <div className="hint" style={{ marginBottom: 16 }}>
            Цены — из переноса №{refs.data.last_import.id} от{' '}
            {fmtDate(refs.data.last_import.started_at, true)} (
            {refs.data.last_import.files.map((f) => f.file).join(', ')}). Считать КП можно
            по {refs.data.counts.quotable}{' '}
            {plural(refs.data.counts.quotable, 'позиции', 'позициям', 'позициям')} из{' '}
            {refs.data.counts.items}: у остальных
            нет рублёвой розничной цены. {refs.data.data_notes.no_fx}
          </div>
        ) : (
          <div className="notice err">
            Прайсы в ядро не залиты — считать КП нечем. Нужен прогон переноса.
          </div>
        )}

        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Шапка предложения</h2>
          <div className="filters">
            <div className="f" style={{ flex: 2, minWidth: 220 }}>
              <label htmlFor="kp-client">Клиент</label>
              <input id="kp-client" value={ш.клиент}
                     onChange={(e) => setШ({ ...ш, клиент: e.target.value })}
                     placeholder="как писать в шапке КП" />
            </div>
            <div className="f">
              <label htmlFor="kp-no">№ КП</label>
              <input id="kp-no" value={ш.номер}
                     onChange={(e) => setШ({ ...ш, номер: e.target.value })} />
            </div>
            <div className="f">
              <label htmlFor="kp-date">Дата КП</label>
              <input id="kp-date" type="date" value={дата}
                     onChange={(e) => setДата(e.target.value)} />
            </div>
            <div className="f" style={{ minWidth: 160 }}>
              <label htmlFor="kp-offer">Вид документа</label>
              <select id="kp-offer" value={оферта ? 'offer' : 'no'}
                      onChange={(e) => setОферта(e.target.value === 'offer')}>
                <option value="offer">оферта (основание для оплаты)</option>
                <option value="no">не оферта</option>
              </select>
            </div>
            <div className="f" style={{ minWidth: 170 }}>
              <label htmlFor="kp-mgr">Менеджер (подпись)</label>
              <input id="kp-mgr" value={ш.менеджер}
                     onChange={(e) => setШ({ ...ш, менеджер: e.target.value })} />
            </div>
            <div className="f" style={{ minWidth: 190 }}>
              <label htmlFor="kp-mail">Почта менеджера</label>
              <input id="kp-mail" value={ш.почта}
                     onChange={(e) => setШ({ ...ш, почта: e.target.value })} />
            </div>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Реквизиты из сделки сюда пока не подтягиваются: это отдельный запрос в ядро,
            и он появится вместе с сохранением КП. Поэтому в бланке у заказчика будет
            только название — ИНН, КПП и адрес CRM здесь взять негде, и на листе об этом
            написано. Дата подставлена сегодняшняя: поменяйте, если КП датируется другим днём.
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Условия расчёта</h2>
          <div className="filters">
            <div className="f">
              <label htmlFor="kp-mk">Наценка к цене прайса, %</label>
              <input id="kp-mk" type="number" min={0} step={0.5} value={у.markupPct}
                     onChange={(e) => setУ({ ...у, markupPct: Math.max(0, Number(e.target.value) || 0) })} />
            </div>
            <div className="f">
              <label htmlFor="kp-disc">Скидка от суммы, %</label>
              <input id="kp-disc" type="number" min={0} max={100} step={0.5} value={у.discountPct}
                     onChange={(e) => setУ({
                       ...у,
                       discountPct: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                     })} />
            </div>
            <div className="f" style={{ minWidth: 150 }}>
              <label htmlFor="kp-mode">Цены в КП</label>
              <select id="kp-mode" value={у.vatMode}
                      onChange={(e) => setУ({ ...у, vatMode: e.target.value as УсловияКП['vatMode'] })}>
                <option value="gross">с НДС</option>
                <option value="net">без НДС</option>
              </select>
            </div>
            <div className="f" style={{ minWidth: 130 }}>
              <label htmlFor="kp-rate">Ставка НДС</label>
              <select id="kp-rate" value={у.vatRate}
                      onChange={(e) => setУ({ ...у, vatRate: Number(e.target.value) })}>
                {СТАВКИ_НДС.map((r) => (
                  <option key={r} value={r}>{r === 0 ? 'без НДС' : `${r}%`}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Прайс СиР — цены с НДС. «Без НДС» пересчитывает цену делением на ставку,
            а не вычитанием процента — как на портале 55, чтобы суммы совпадали.
            Наценка на цены, введённые руками, не применяется. {подписьНДС(у)}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Найти в прайсе</h2>
          <div className="filters">
            <div className="f" style={{ flex: 1, minWidth: 260 }}>
              <label htmlFor="kp-q">Название или артикул</label>
              <input id="kp-q" value={ввод} autoFocus
                     onChange={(e) => setВвод(e.target.value)}
                     placeholder={`минимум ${MIN_QUERY} символа`} />
            </div>
          </div>

          {короткий ? (
            <div className="hint" style={{ marginTop: 10 }}>
              Введите минимум {MIN_QUERY} символа. Позицию, которой нет в прайсе, добавьте
              вручную ниже.
            </div>
          ) : (
            <QueryState isLoading={поиск.isLoading} error={поиск.error}>
              {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
              <div className="table-wrap cards" style={{ marginTop: 10 }}>
                <table>
                  <thead>
                    <tr>
                      <th className="nowrap">Артикул</th>
                      <th>Наименование</th>
                      <th className="num">Розница</th>
                      <th className="nowrap">Прайс-лист</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(поиск.data?.rows ?? []).map((r) => (
                      <tr key={r.src_key}>
                        <td className="mono nowrap" data-label="Артикул">
                          {r.pn || <span className="muted">—</span>}
                        </td>
                        <td data-label="Наименование">{r.name}</td>
                        <td className="num" data-label="Розница">
                          {цена(r.price_user, r.cur_user)}
                          {!годнаДляКП(r) && <div className="hint">{почемуНеГодна(r)}</div>}
                        </td>
                        <td className="nowrap" style={{ fontSize: 12 }} data-label="Прайс-лист">
                          {r.list_name}
                        </td>
                        {/* Колонка кнопки безымянна и в шапке — пустая подпись, а не забытая. */}
                        <td data-label="">
                          <button onClick={() => добавить(r)}>＋ в КП</button>
                        </td>
                      </tr>
                    ))}
                    {(поиск.data?.rows.length ?? 0) === 0 && (
                      <tr>
                        <td colSpan={5} className="empty">
                          <div className="empty-title">В прайсах не нашлось</div>
                          <div className="empty-hint">Добавьте позицию вручную ниже.</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {поиск.data && поиск.data.total > НАЙТИ && (
                <div className="hint" style={{ marginTop: 6 }}>
                  Найдено {поиск.data.total}, показаны первые {НАЙТИ} — уточните запрос
                  или отберите позиции в разделе «Прайсы».
                </div>
              )}
            </QueryState>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Позиция не из прайса</h2>
          <div className="filters">
            <div className="f" style={{ flex: 2, minWidth: 220 }}>
              <label htmlFor="kp-mn">Наименование</label>
              <input id="kp-mn" value={ручноеИмя}
                     onChange={(e) => setРучноеИмя(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') добавитьРучную() }} />
            </div>
            <div className="f">
              <label htmlFor="kp-mq">Кол-во</label>
              <input id="kp-mq" type="number" min={1} value={ручноеКол}
                     onChange={(e) => setРучноеКол(e.target.value)} />
            </div>
            <div className="f">
              <label htmlFor="kp-mp">Цена, ₽</label>
              <input id="kp-mp" value={ручнаяЦена}
                     onChange={(e) => setРучнаяЦена(e.target.value)} />
            </div>
            <div className="f">
              <label>&nbsp;</label>
              <button onClick={добавитьРучную} disabled={!ручноеИмя.trim()}>＋ добавить</button>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Предложение</h2>
          {строки.length === 0 ? (
            <div className="empty">
              <div className="empty-title">КП пустое</div>
              <div className="empty-hint">
                Найдите позиции в прайсе выше или добавьте их вручную.
              </div>
            </div>
          ) : (
            <>
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th>Наименование</th>
                      <th className="nowrap">Артикул</th>
                      <th className="num">Цена прайса</th>
                      <th className="num">Цена в КП, ₽</th>
                      <th className="num">Кол-во</th>
                      <th className="num">Сумма</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {строки.map((с) => {
                      const p = ценаСтроки(с, у)
                      const почему = почемуНеГодна({
                        price_user: с.base, cur_user: с.cur, problems: с.problems,
                      })
                      return (
                        <tr key={с.key}>
                          <td data-label="Наименование">
                            {с.name}
                            {с.сама && <span className="badge" style={{ marginLeft: 6 }}>вручную</span>}
                            {с.problems.length > 0 && (
                              <div className="hint">{с.problems.join(', ').replace(/_/g, ' ')}</div>
                            )}
                          </td>
                          <td className="mono nowrap" data-label="Артикул">
                            {с.pn || <span className="muted">—</span>}
                          </td>
                          <td className="num" data-label="Цена прайса">
                            {цена(с.base, с.cur)}
                            {почему && <div className="hint">{почему}</div>}
                          </td>
                          {/* В карточке подпись колонки печатается над полем ввода, и поле
                              занимает всю ширину — цену правят с телефона так же, как с монитора. */}
                          <td className="num" style={{ minWidth: 130 }} data-label="Цена в КП, ₽">
                            <input
                              value={с.manual === null ? '' : String(с.manual)}
                              placeholder={p === null ? 'введите цену' : String(p)}
                              onChange={(e) => {
                                const v = e.target.value.trim().replace(',', '.')
                                const n = Number(v)
                                править(с.key, {
                                  manual: v === '' || !Number.isFinite(n) ? null : n,
                                })
                              }}
                              style={{ textAlign: 'right' }}
                            />
                          </td>
                          <td className="num" style={{ minWidth: 90 }} data-label="Кол-во">
                            <input type="number" min={1} value={с.qty}
                                   onChange={(e) => править(с.key, {
                                     qty: Math.max(1, Number(e.target.value) || 1),
                                   })}
                                   style={{ textAlign: 'right' }} />
                          </td>
                          <td className="num" data-label="Сумма">
                            {p === null
                              ? <span className="error">не в итоге</span>
                              : цена(копейки(p * с.qty), 'RUB')}
                          </td>
                          {/* Колонка крестика безымянна и в шапке — пустая подпись, а не забытая. */}
                          <td data-label="">
                            <button onClick={() => убрать(с.key)} title="убрать из КП">×</button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="tiles" style={{ marginTop: 16 }}>
                <div className="tile">
                  <div className="t-label">Сумма</div>
                  <div className="t-sum">{цена(итоги.сумма, 'RUB')}</div>
                </div>
                <div className="tile">
                  <div className="t-label">Скидка {у.discountPct || 0}%</div>
                  <div className="t-sum">−{цена(итоги.скидка, 'RUB')}</div>
                </div>
                <div className="tile">
                  <div className="t-label">
                    {у.vatRate === 0
                      ? 'НДС не облагается'
                      : у.vatMode === 'gross' ? `в том числе НДС ${у.vatRate}%` : `НДС ${у.vatRate}% сверху`}
                  </div>
                  <div className="t-sum">{цена(итоги.ндс, 'RUB')}</div>
                </div>
                <div className={`tile${итоги.безЦены > 0 ? ' hot' : ''}`}>
                  <div className="t-label">Итого</div>
                  <div className="t-sum">{цена(итоги.итог, 'RUB')}</div>
                </div>
              </div>

              {итоги.безЦены > 0 && (
                <div className="notice err" style={{ marginTop: 12, marginBottom: 0 }}>
                  <b>У {итоги.безЦены} позиц. цены нет, и в итог они НЕ включены.</b>{' '}
                  Ноль вместо цены занизил бы предложение молча, поэтому такие строки
                  считаются только после того, как цену впишут рублями в колонку «Цена в КП».
                </div>
              )}

              <div className="hint" style={{ marginTop: 10 }}>
                Порядок расчёта: цена прайса × наценка → сумма → скидка → НДС. Скидка
                применяется до НДС: иначе при «цены с НДС» налог считался бы от суммы,
                которую клиент не платит.
              </div>
              <div className="hint">
                Суммы показаны без копеек — это денежный формат всей CRM. Считаются они
                точно: у 27 позиций прайса (замер 28.07.2026) в цене есть копейки, и они не теряются.
              </div>
            </>
          )}
        </div>

        <div className="card">
          <h2>Условия и текст для письма</h2>
          <div className="filters">
            <div className="f" style={{ flex: 1, minWidth: 240 }}>
              <label htmlFor="kp-pay">Условия оплаты</label>
              <input id="kp-pay" value={ш.условияОплаты}
                     onChange={(e) => setШ({ ...ш, условияОплаты: e.target.value })} />
            </div>
            <div className="f" style={{ flex: 1, minWidth: 240 }}>
              <label htmlFor="kp-del">Условия поставки</label>
              <input id="kp-del" value={ш.условияПоставки}
                     onChange={(e) => setШ({ ...ш, условияПоставки: e.target.value })} />
            </div>
          </div>
          <div className="f" style={{ marginTop: 12 }}>
            <label htmlFor="kp-other">Прочие условия</label>
            <textarea id="kp-other" rows={3} value={ш.прочее}
                      onChange={(e) => setШ({ ...ш, прочее: e.target.value })} />
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Умолчания по оплате и поставке взяты с портала 55. Свои шаблоны текстовок
            (они лежат в хранилище портала) появятся вместе с сохранением КП.
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="primary" onClick={напечатать} disabled={!строки.length}>
              🖨 Печать бланка КП
            </button>
            <button onClick={вWord} disabled={!строки.length}>
              📄 В Word (.doc)
            </button>
            <button onClick={() => void скопировать()} disabled={!строки.length}>
              Скопировать текст КП
            </button>
            {скопировано && <span className="hint">{скопировано}</span>}
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Бланк — копия печатной формы КП портала 55, снятая 28.07.2026. Лист открывается
            отдельной страницей с кнопкой «Печать / Сохранить в PDF»; «В Word» даёт файл .doc,
            который можно править. На листе печатается служебная отметка CRM: что это не
            документ системы, чего в нём нет и почему. Позиции без цены попадают на лист
            прочерком и в итог не входят — молча выбросить их значило бы отдать клиенту
            предложение без части позиций.
            {итоги.безЦены > 0 && (
              <> Сейчас таких{' '}
                {итоги.безЦены}{' '}
                {plural(итоги.безЦены, 'позиция', 'позиции', 'позиций')}.
              </>
            )}
          </div>
          {бедаПечати && <div className="notice err">{бедаПечати}</div>}

          <div className="f" style={{ marginTop: 12 }}>
            <label htmlFor="kp-text">Текст предложения</label>
            <textarea id="kp-text" rows={14} readOnly value={текст}
                      className="mono" onFocus={(e) => e.currentTarget.select()} />
          </div>
          <div className="hint">
            Текст только показывается: CRM его никуда не отправляет и не сохраняет.
          </div>
        </div>
      </QueryState>
    </>
  )
}
